import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/shared/PageHero";
import CTASection from "@/components/shared/CTASection";
import SustainabilityCalculator from "@/components/sustainability/SustainabilityCalculator";
import BrandCard from "@/components/sustainability/BrandCard";
import ColorfulStatCard from "@/components/shared/ColorfulStatCard";
import VibrantSectionHeader from "@/components/shared/VibrantSectionHeader";
import SDGBadge from "@/components/shared/SDGBadge";
import PageSEO from "@/components/shared/PageSEO";
import Breadcrumbs from "@/components/shared/Breadcrumbs";
import saudiHeritageVideo from "@/assets/saudi-heritage-video.mp4";
import { Leaf, Zap, Globe, Target, Recycle, Users, BarChart3, TreePine, Building2, Heart, Shield, Calendar, Sparkles, Award } from "lucide-react";

const brands = [
  {
    name: "LEMO",
    tagline: "Event Mobility Solutions",
    description: "Premium B2B electric scooter rentals for events, conferences & exhibitions. We deliver, you enjoy, we collect. Zero-emission mobility that makes your events memorable and sustainable.",
    badge: "Now Booking",
    badgeColor: "green" as const,
    highlights: [
      { icon: Zap, label: "Zero Emissions" },
      { icon: Shield, label: "Fully Insured" },
      { icon: Calendar, label: "Event Support" },
    ],
    ctaText: "Book Scooters",
    ctaLink: "https://lemo.sdcgulf.com/",
    accentColor: "#4ade80",
  },
  {
    name: "SOHOB",
    tagline: "Sustainable Paper Products",
    description: "Softness inspired by clouds. Premium eco-friendly paper products crafted for comfort, designed for your family. Sustainable choices for everyday essentials.",
    badge: "Coming Soon",
    badgeColor: "blue" as const,
    highlights: [
      { icon: Leaf, label: "Eco-Friendly" },
      { icon: Award, label: "Premium Quality" },
      { icon: Sparkles, label: "Sustainable" },
    ],
    ctaText: "Register Interest",
    ctaLink: "https://sohob.sdcgulf.com/",
    accentColor: "#60a5fa",
  },
];

const stats = [
  { value: "500+", label: "Tons CO₂ Saved", icon: Leaf, color: "green" as const },
  { value: "100%", label: "Electric Fleet", icon: Zap, color: "blue" as const },
  { value: "50+", label: "Green Projects", icon: Globe, color: "teal" as const },
  { value: "2030", label: "Vision Aligned", icon: Target, color: "gold" as const },
];

const initiatives = [
  {
    icon: Zap,
    title: "Electric Mobility",
    description: "Our entire fleet operates on 100% electric power, eliminating direct emissions and reducing our carbon footprint significantly.",
    color: "blue" as const,
  },
  {
    icon: Recycle,
    title: "Eco Products (SOHOB)",
    description: "SOHOB brings sustainable, eco-friendly products designed to minimize environmental impact while maximizing utility.",
    color: "green" as const,
  },
  {
    icon: TreePine,
    title: "Green Solutions",
    description: "From solar-powered charging stations to recyclable materials, we integrate sustainability into every aspect of our operations.",
    color: "teal" as const,
  },
  {
    icon: Target,
    title: "Vision 2030 Alignment",
    description: "Every initiative we undertake is designed to support Saudi Arabia's ambitious Vision 2030 sustainability goals.",
    color: "gold" as const,
  },
];

const vision2030Pillars = [
  {
    icon: Building2,
    title: "Diversified Economy",
    description: "Supporting economic diversification through innovative mobility solutions and new business models.",
  },
  {
    icon: Zap,
    title: "Smart Mobility",
    description: "Deploying cutting-edge technology for efficient, connected, and sustainable transportation systems.",
  },
  {
    icon: Heart,
    title: "Quality of Life",
    description: "Enhancing urban living through accessible, convenient, and healthy mobility options.",
  },
  {
    icon: Leaf,
    title: "Environmental Stewardship",
    description: "Leading the transition to zero-emission transportation and sustainable urban development.",
  },
];

const commitments = [
  { icon: Zap, title: "Zero Emission Fleet", description: "100% electric vehicles by 2025", color: "blue" },
  { icon: Recycle, title: "Sustainable Supply Chain", description: "Partnering only with eco-conscious suppliers", color: "green" },
  { icon: Users, title: "Community Impact", description: "Creating green jobs and skills training", color: "purple" },
  { icon: BarChart3, title: "Continuous Innovation", description: "R&D investment in clean technologies", color: "teal" },
  { icon: Globe, title: "Circular Economy", description: "Maximizing resource efficiency and recycling", color: "green" },
  { icon: Target, title: "Transparency & Reporting", description: "Annual sustainability impact reports", color: "gold" },
];

const sdgGoals = [
  { number: 7, title: "Affordable & Clean Energy", description: "Promoting renewable energy in transportation" },
  { number: 9, title: "Industry, Innovation & Infrastructure", description: "Building sustainable mobility infrastructure" },
  { number: 11, title: "Sustainable Cities & Communities", description: "Creating livable, accessible urban spaces" },
  { number: 12, title: "Responsible Consumption & Production", description: "Minimizing waste and maximizing efficiency" },
  { number: 13, title: "Climate Action", description: "Reducing emissions and fighting climate change" },
];

const colorMap: Record<string, string> = {
  blue: "from-sky-blue/20 to-sky-blue/5 border-sky-blue/20 hover:border-sky-blue/40",
  green: "from-eco-green/20 to-eco-green/5 border-eco-green/20 hover:border-eco-green/40",
  purple: "from-purple/20 to-purple/5 border-purple/20 hover:border-purple/40",
  teal: "from-teal-bright/20 to-teal-bright/5 border-teal-bright/20 hover:border-teal-bright/40",
  gold: "from-primary/20 to-amber-glow/5 border-primary/20 hover:border-primary/40",
};

const iconColorMap: Record<string, string> = {
  blue: "text-sky-blue",
  green: "text-eco-green",
  purple: "text-purple",
  teal: "text-teal-bright",
  gold: "text-primary",
};

const iconBgMap: Record<string, string> = {
  blue: "bg-gradient-to-br from-sky-blue to-sky-blue-light",
  green: "bg-gradient-to-br from-eco-green to-eco-green-light",
  purple: "bg-gradient-to-br from-purple to-rose",
  teal: "bg-gradient-to-br from-teal-bright to-eco-green",
  gold: "bg-gradient-to-br from-primary to-amber-glow",
};

const Sustainability = () => {
  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="Green Mobility & Sustainability KSA"
        description="Sustainable micro mobility in Saudi Arabia — electric fleets, eco-friendly products & Vision 2030. الاستدامة والتنقل الأخضر."
        keywords="green mobility KSA, sustainability Vision 2030, طاقة نظيفة, الاستدامة السعودية, electric fleet Saudi Arabia, carbon neutral mobility, eco-friendly transportation KSA, sustainable development Saudi, تنقل أخضر, مركبات كهربائية صديقة للبيئة"
      />
      <Navbar />

      <PageHero
        title="Sustainability is Who We Are"
        subtitle="Green Mobility"
        description="At SDC, sustainability isn't just a goal—it's our foundation. We're committed to transforming urban mobility while protecting our planet for future generations."
        variant="vibrant"
      />

      <Breadcrumbs />

      {/* Stats Section - Colorful */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-eco-green/5 via-transparent to-sky-blue/5" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <ColorfulStatCard
                key={index}
                value={stat.value}
                label={stat.label}
                icon={stat.icon}
                color={stat.color}
                delay={index * 100}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Our Sustainable Brands */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-secondary/50 to-secondary/30" />
        <div className="absolute -left-40 top-1/2 w-80 h-80 rounded-full bg-eco-green/10 blur-3xl" />
        <div className="absolute -right-40 top-1/2 w-80 h-80 rounded-full bg-sky-blue/10 blur-3xl" />
        
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Our Brands"
            title="Our Sustainable Brands"
            description="Pioneering eco-friendly solutions across mobility and everyday products"
            badgeColor="green"
          />

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {brands.map((brand, index) => (
              <BrandCard key={index} {...brand} />
            ))}
          </div>
        </div>
      </section>

      {/* Green Initiatives */}
      <section className="py-20 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <VibrantSectionHeader
            badge="Our Approach"
            title="Green Initiatives"
            description="Every aspect of our business is designed with environmental responsibility at its core."
            badgeColor="green"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {initiatives.map((initiative, index) => (
              <div
                key={index}
                className={`group p-6 rounded-2xl bg-gradient-to-br ${colorMap[initiative.color]} border transition-all duration-500 hover:-translate-y-2 hover:shadow-xl relative overflow-hidden`}
              >
                {/* Glow effect */}
                <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full ${iconBgMap[initiative.color]} blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />
                
                <div className="relative z-10">
                  <div className={`w-14 h-14 rounded-xl ${iconBgMap[initiative.color]} flex items-center justify-center mb-4 shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <initiative.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{initiative.title}</h3>
                  <p className="text-sm text-muted-foreground">{initiative.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision 2030 Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src={saudiHeritageVideo}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/70 to-background/90" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-amber-glow/20 text-primary text-sm font-semibold mb-4 border border-primary/20">
                National Alignment
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Vision 2030 Partnership
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                SDC is proud to be a key contributor to Saudi Arabia's Vision 2030 sustainability goals. 
                Our initiatives directly support the Kingdom's ambitious transformation agenda, focusing on 
                environmental protection, economic diversification, and improved quality of life.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {vision2030Pillars.map((pillar, index) => (
                  <div key={index} className="flex items-start gap-3 group">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-amber-glow flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      <pillar.icon className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{pillar.title}</h4>
                      <p className="text-xs text-muted-foreground">{pillar.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative flex justify-center">
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-3xl bg-gradient-to-br from-primary/30 via-amber-glow/20 to-primary/10 backdrop-blur-sm flex items-center justify-center relative overflow-hidden border border-primary/20">
                {/* Animated rings */}
                <div className="absolute inset-4 rounded-full border border-primary/30 animate-spin-slow" />
                <div className="absolute inset-8 rounded-full border border-primary/40 animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '30s' }} />
                
                <div className="text-center relative z-10">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-primary via-amber-glow to-primary bg-clip-text text-transparent mb-1">2030</div>
                  <div className="text-sm text-muted-foreground font-medium">Vision Aligned</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Calculator */}
      <SustainabilityCalculator />

      {/* Sustainability Commitments */}
      <section className="py-20 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <VibrantSectionHeader
            badge="Our Promise"
            title="Sustainability Commitments"
            badgeColor="purple"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commitments.map((commitment, index) => (
              <div
                key={index}
                className={`group p-6 rounded-2xl bg-gradient-to-br ${colorMap[commitment.color]} border transition-all duration-500 hover:-translate-y-1 hover:shadow-xl flex items-start gap-4 relative overflow-hidden`}
              >
                <div className={`absolute -top-10 -right-10 w-24 h-24 rounded-full ${iconBgMap[commitment.color]} blur-3xl opacity-0 group-hover:opacity-20 transition-opacity`} />
                
                <div className={`w-12 h-12 rounded-xl ${iconBgMap[commitment.color]} flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform`}>
                  <commitment.icon className="w-6 h-6 text-white" />
                </div>
                <div className="relative z-10">
                  <h3 className="font-semibold text-foreground mb-1">{commitment.title}</h3>
                  <p className="text-sm text-muted-foreground">{commitment.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UN SDG Goals */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-secondary/50 to-secondary/30" />
        
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Global Impact"
            title="UN Sustainable Development Goals"
            description="Our work contributes to multiple UN SDGs, driving global sustainability forward."
            badgeColor="blue"
          />

          <div className="grid md:grid-cols-5 gap-4">
            {sdgGoals.map((goal) => (
              <SDGBadge
                key={goal.number}
                number={goal.number}
                title={goal.title}
                description={goal.description}
              />
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Join Our Sustainability Journey"
        description="Partner with SDC to create a greener, more sustainable future for urban mobility."
        primaryButtonText="Get Started"
        primaryButtonLink="/contact"
      />

      <Footer />
    </div>
  );
};

export default Sustainability;
